/**
 * ALDER SEARCH — Executive Search Practice Page
 * Route: /executive-search
 * Brief: File 02 of 13 (v3.0)
 * Design: "Alder Forest — Organic Precision" — matches existing site exactly
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { media } from "@/data/media";

const services = [
  {
    number: "01",
    title: "CPO Search",
    description:
      "The CPO who can own a product roadmap and be accountable for the commercial outcomes that flow from it is a specific type of executive. We place CPOs who've operated at the intersection of product strategy and P&L responsibility in VC- and PE-backed businesses.",
    tags: ["CPO", "Product Strategy", "Commercial Accountability"],
    link: "/executive-search/cpo-search",
  },
  {
    number: "02",
    title: "Product Director Search",
    description:
      "Product Directors who can connect product direction to customer outcomes and commercial performance are a specific type of leader. We place product leaders who can build the function and raise the quality of product execution in VC- and PE-backed businesses.",
    tags: ["Product Direction", "Product Strategy", "Customer Outcomes"],
    link: "/senior-leadership/head-of-product-search",
  },
  {
    number: "03",
    title: "Product Board Director Placement",
    description:
      "An independent product director placed well adds governance credibility, functional depth, and a perspective neither the sponsor nor the management team can give themselves. We place directors who earn their seat.",
    tags: ["Product", "Independent Directors", "Governance"],
    link: "/executive-search/board-director-placement",
  },
  {
    number: "04",
    title: "Product Leadership Team Buildouts",
    description:
      "Platform acquisitions need a coordinated product leadership programme, not a series of disconnected searches. We plan and execute multi-role buildouts aligned to your 100-day plan.",
    tags: ["Product", "100-Day Plans", "Team Buildouts"],
    link: "/executive-search/leadership-team-buildouts",
  },
  {
    number: "05",
    title: "Fractional & Interim Product Leadership",
    description:
      "When a portfolio company needs product leadership now, we place VC- and PE-ready leaders who step in at pace. No onboarding curve. No learning the investor model. Operational from the first board meeting.",
    tags: ["Fractional CPO", "Interim Product", "Bridge Leadership"],
    link: "/executive-search/fractional-interim-leadership",
  },
];

const statsData = [
  { value: 300, suffix: "+", label: "Placements Completed" },
  { value: 92, suffix: "%", label: "Placement Retention Rate" },
  { value: 60, suffix: "+", label: "VC & PE Sponsors Served" },
  { value: 15, suffix: "+", label: "Years in VC & PE" },
];

function CountUp({ target, suffix }: { target: number; suffix: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const duration = 1800;
            const steps = 60;
            const increment = target / steps;
            let current = 0;
            const timer = setInterval(() => {
              current += increment;
              if (current >= target) {
                setCount(target);
                clearInterval(timer);
              } else {
                setCount(Math.floor(current));
              }
            }, duration / steps);
          }
        });
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref}>
      {count}
      {suffix}
    </span>
  );
}

export default function ExecutiveSearchPage() {
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const sectionsRef = useRef<(HTMLDivElement | null)[]>([]);

  // SEO meta tags
  useEffect(() => {
    document.title = "Product Executive Search for VC- and PE-Backed Companies | Alder Search";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Retained product executive and board search exclusively for VC and PE investors and portfolio companies. CPOs, product leaders, and product-focused board directors."
      );
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogTitle) ogTitle.setAttribute("content", "Product Executive Search for VC- and PE-Backed Companies | Alder Search");
    if (ogDesc) ogDesc.setAttribute("content", "Retained product executive and board search exclusively for VC and PE investors and portfolio companies. CPOs, product leaders, and product-focused board directors.");
    if (ogUrl) ogUrl.setAttribute("content", "https://aldersearch.com/executive-search");
    return () => {
      document.title = "Alder Search | Retained Product Executive Search for VC and PE";
    };
  }, []);

  // Scroll reveal
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const allEls = [...cardsRef.current, ...sectionsRef.current];
    if (prefersReduced) {
      allEls.forEach((el) => {
        if (!el) return;
        el.style.opacity = "1";
        el.style.transform = "none";
      });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    allEls.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(28px)";
      el.style.transition = `opacity 0.7s ease-out ${i * 70}ms, transform 0.7s ease-out ${i * 70}ms`;
      observer.observe(el);
    });
    const fallback = setTimeout(() => {
      allEls.forEach((el) => {
        if (!el) return;
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      });
    }, 2000);
    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "oklch(0.978 0.006 80)" }}>
      <Navigation />

      {/* ── HERO ── */}
      <section
        className="relative min-h-[70vh] flex items-center overflow-hidden"
        style={{ background: "oklch(0.245 0.065 155)" }}
      >
        <picture className="absolute inset-0 block opacity-[0.18]">
          <source type="image/avif" srcSet={media.hero.avif} />
          <img src={media.hero.webp} srcSet={media.hero.srcSet} sizes="100vw" alt="" aria-hidden="true" width={media.hero.width} height={media.hero.height} className="h-full w-full object-cover" style={{ objectPosition: "center 30%" }} />
        </picture>
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(to bottom, oklch(0.245 0.065 155 / 0.7) 0%, oklch(0.245 0.065 155 / 0.95) 100%)",
          }}
        />

        <div className="container relative z-10 pt-32 pb-24">
          <div className="max-w-3xl">
            {/* Eyebrow */}
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
              <span
                className="font-ui text-xs tracking-widest uppercase"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  color: "oklch(0.62 0.085 75)",
                  letterSpacing: "0.18em",
                }}
              >
                Alder Search
              </span>
            </div>

            {/* H1 */}
            <h1
              className="font-display mb-6"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 700,
                fontSize: "clamp(2.4rem, 5vw, 4rem)",
                color: "oklch(0.952 0.012 80)",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
              }}
            >
              Product Leadership Search
              <br />
              <em style={{ fontStyle: "italic", color: "oklch(0.72 0.09 75)" }}>
                Built for VC &amp; PE.
              </em>
            </h1>

            {/* Subheading */}
            <p
              className="font-body mb-10 max-w-2xl"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontWeight: 400,
                fontSize: "1.1rem",
                lineHeight: 1.75,
                color: "oklch(0.75 0.02 155)",
              }}
            >
              Retained product executive and board search. Exclusively for VC and PE investors and their portfolio
              companies. We place the CPOs, product leaders, and product-focused board directors who execute your value creation thesis — from day
              one through to exit.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <Link
                href="/#contact"
                className="font-ui text-sm font-600 px-8 py-3.5 transition-all duration-300 inline-block"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  background: "oklch(0.62 0.085 75)",
                  color: "oklch(0.245 0.065 155)",
                  borderRadius: "2px",
                  textDecoration: "none",
                }}
              >
                Engage Alder Search
              </Link>
              <Link
                href="/approach"
                className="font-ui text-sm font-500 px-8 py-3.5 transition-all duration-300 inline-block"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  fontWeight: 500,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  border: "1px solid oklch(0.62 0.085 75 / 0.5)",
                  color: "oklch(0.85 0.015 80)",
                  borderRadius: "2px",
                  textDecoration: "none",
                }}
              >
                Our Approach
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section className="py-16" style={{ background: "oklch(0.18 0.055 155)" }}>
        <div className="container">
          <div
            ref={(el) => { sectionsRef.current[0] = el; }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {statsData.map((stat) => (
              <div key={stat.label} className="flex flex-col items-center text-center gap-2">
                <div
                  className="font-display"
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontWeight: 700,
                    fontSize: "clamp(2rem, 4vw, 3rem)",
                    color: "oklch(0.72 0.09 75)",
                    lineHeight: 1,
                  }}
                >
                  <CountUp target={stat.value} suffix={stat.suffix} />
                </div>
                <div
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    fontWeight: 400,
                    fontSize: "0.72rem",
                    color: "oklch(0.55 0.02 155)",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES SECTION ── */}
      <section className="py-28" style={{ background: "oklch(0.978 0.006 80)" }}>
        <div className="container">
          {/* Section Header */}
          <div
            ref={(el) => { sectionsRef.current[1] = el; }}
            className="mb-16"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
              <span
                className="font-ui text-xs tracking-widest uppercase"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  color: "oklch(0.62 0.085 75)",
                  letterSpacing: "0.18em",
                }}
              >
                What We Do
              </span>
            </div>
            <h2
              className="font-display mb-4 max-w-2xl"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 700,
                fontSize: "clamp(2rem, 4vw, 3.2rem)",
                color: "oklch(0.245 0.065 155)",
                lineHeight: 1.15,
                letterSpacing: "-0.015em",
              }}
            >
              Search Services Built for VC &amp; PE
            </h2>
            <p
              className="font-body max-w-2xl"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontWeight: 400,
                fontSize: "1.05rem",
                lineHeight: 1.75,
                color: "oklch(0.45 0.015 155)",
              }}
            >
              Every mandate is retained and exclusive. We don't contingency search. Our full
              attention, our network, and our methodology are deployed on your behalf from day one.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <div
                key={service.number}
                ref={(el) => { cardsRef.current[i] = el; }}
                className="group relative p-8 flex flex-col"
                style={{
                  background: "white",
                  border: "1px solid oklch(0.88 0.018 80)",
                  borderRadius: "4px",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = "oklch(0.62 0.085 75)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = "oklch(0.88 0.018 80)";
                }}
              >
                <div
                  className="font-mono-accent mb-5"
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontWeight: 300,
                    fontSize: "0.75rem",
                    color: "oklch(0.62 0.085 75)",
                    letterSpacing: "0.1em",
                  }}
                >
                  {service.number}
                </div>
                <h3
                  className="font-display mb-3"
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontWeight: 600,
                    fontSize: "1.2rem",
                    color: "oklch(0.245 0.065 155)",
                    lineHeight: 1.3,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {service.title}
                </h3>
                <p
                  className="font-body mb-6 flex-1"
                  style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    fontWeight: 400,
                    fontSize: "0.9rem",
                    lineHeight: 1.75,
                    color: "oklch(0.45 0.015 155)",
                  }}
                >
                  {service.description}
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {service.tags.map((tag) => (
                    <span
                      key={tag}
                      className="font-ui text-xs px-2.5 py-1"
                      style={{
                        fontFamily: "'DM Sans', system-ui, sans-serif",
                        fontWeight: 500,
                        fontSize: "0.68rem",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        background: "oklch(0.952 0.012 80)",
                        color: "oklch(0.32 0.07 155)",
                        borderRadius: "2px",
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <Link
                  href={service.link}
                  className="font-ui text-sm inline-flex items-center gap-1.5 transition-colors duration-200"
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    letterSpacing: "0.04em",
                    color: "oklch(0.245 0.065 155)",
                    textDecoration: "none",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.245 0.065 155)";
                  }}
                >
                  Learn more →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PULL QUOTE ── */}
      <section
        className="py-24 relative overflow-hidden"
        style={{ background: "oklch(0.245 0.065 155)" }}
      >
        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle, oklch(0.62 0.085 75) 1px, transparent 1px)`,
            backgroundSize: "32px 32px",
          }}
        />
        <div className="container relative z-10">
          <div
            ref={(el) => { sectionsRef.current[2] = el; }}
            className="max-w-3xl mx-auto text-center"
          >
            {/* Opening quote mark */}
            <div
              className="font-display mb-6 select-none"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: "5rem",
                lineHeight: 0.6,
                color: "oklch(0.62 0.085 75)",
                opacity: 0.6,
              }}
            >
              "
            </div>
            <blockquote
              className="font-display mb-8"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 400,
                fontStyle: "italic",
                fontSize: "clamp(1.2rem, 2.5vw, 1.65rem)",
                lineHeight: 1.55,
                color: "oklch(0.952 0.012 80)",
                letterSpacing: "-0.01em",
              }}
            >
              The right CEO at a portfolio company is worth more than any operational improvement.
              We treat every search as if the entire investment depends on it — because it does.
            </blockquote>
            <div
              className="flex items-center justify-center gap-3"
            >
              <div className="h-px w-8" style={{ background: "oklch(0.62 0.085 75)" }} />
              <cite
                className="font-ui not-italic text-sm"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  fontWeight: 500,
                  fontSize: "0.82rem",
                  color: "oklch(0.62 0.085 75)",
                  letterSpacing: "0.06em",
                }}
              >
                Ryan Ollerenshaw, Founding Partner, Alder Search
              </cite>
              <div className="h-px w-8" style={{ background: "oklch(0.62 0.085 75)" }} />
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW WE WORK (condensed) ── */}
      <section className="py-24" style={{ background: "oklch(0.978 0.006 80)" }}>
        <div className="container">
          <div
            ref={(el) => { sectionsRef.current[3] = el; }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
              <span
                className="font-ui text-xs tracking-widest uppercase"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  color: "oklch(0.62 0.085 75)",
                  letterSpacing: "0.18em",
                }}
              >
                How We Work
              </span>
            </div>
            <h2
              className="font-display mb-5"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 700,
                fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
                color: "oklch(0.245 0.065 155)",
                lineHeight: 1.15,
                letterSpacing: "-0.015em",
              }}
            >
              A Process Built for High-Stakes Mandates.
            </h2>
            <p
              className="font-body mb-8"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontWeight: 400,
                fontSize: "1.05rem",
                lineHeight: 1.8,
                color: "oklch(0.45 0.015 155)",
              }}
            >
              Every Alder Search mandate runs through the same four-phase process. Mandate
              alignment. Market mapping. Rigorous assessment. Placement and onboarding. The details
              of each phase are calibrated to the specific service — you'll find those on each
              service page.
            </p>
            <Link
              href="/approach"
              className="font-ui text-sm inline-flex items-center gap-2 transition-colors duration-200"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 600,
                fontSize: "0.8rem",
                letterSpacing: "0.05em",
                color: "oklch(0.245 0.065 155)",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.245 0.065 155)";
              }}
            >
              Read our full approach
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ── CTA SECTION ── */}
      <section
        className="py-28 relative overflow-hidden"
        style={{ background: "oklch(0.18 0.055 155)" }}
      >
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle, oklch(0.62 0.085 75) 1px, transparent 1px)`,
            backgroundSize: "32px 32px",
          }}
        />
        <div className="container relative z-10">
          <div
            ref={(el) => { sectionsRef.current[4] = el; }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
              <span
                className="font-ui text-xs tracking-widest uppercase"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  color: "oklch(0.62 0.085 75)",
                  letterSpacing: "0.18em",
                }}
              >
                Engage Alder Search
              </span>
            </div>
            <h2
              className="font-display mb-6"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 700,
                fontSize: "clamp(2rem, 4vw, 3.2rem)",
                color: "oklch(0.952 0.012 80)",
                lineHeight: 1.15,
                letterSpacing: "-0.015em",
              }}
            >
              Start a Confidential Conversation.
            </h2>
            <p
              className="font-body mb-10"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontWeight: 300,
                fontSize: "1.05rem",
                lineHeight: 1.8,
                color: "oklch(0.75 0.02 155)",
              }}
            >
              Whether you have an active mandate, are preparing for a transaction, or want to
              understand how Alder Search can support your portfolio — we welcome the conversation.
            </p>
            <div className="flex flex-wrap gap-4 items-center">
              <Link
                href="/#contact"
                className="font-ui text-sm font-600 px-8 py-3.5 transition-all duration-300 inline-block"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  background: "oklch(0.62 0.085 75)",
                  color: "oklch(0.245 0.065 155)",
                  borderRadius: "2px",
                  textDecoration: "none",
                }}
              >
                Engage Alder Search
              </Link>
              <span
                className="font-ui text-xs"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  fontWeight: 400,
                  fontSize: "0.78rem",
                  color: "oklch(0.62 0.085 75)",
                  letterSpacing: "0.04em",
                  opacity: 0.8,
                }}
              >
                All enquiries are treated with strict confidentiality.
              </span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
